import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'

import './styles.scss'
import { WorkEntryRegistryForm } from '../components/workEntry/WorkEntryRegistryForm'
import { WorkEntryCoordinates } from '../components/workEntry/WorkEntryCoordinates'
import { WorkActivityRegistryForm } from '../components/workEntry/WorkActivityRegistryForm'
import { WarActivityLog } from '../components/workEntry/WarActivityLog'
import TimeEntryWsrTagAutocomplete from '../components/timeEntry/TimeEntryGrid/TimeEntryWsrTagAutocomplete'
import { formatWsrTagLabel, getWsrByTagId } from '../services/WSRService'
import { submitFormSubmission, updateFormSubmission } from '../services/formDefinitionsService'
import type { FormDefinitionDetail } from '../types/Form/FormDefinition'
import type { BillingLogEntryResponse, BillingOrderValues } from '../types/Contracts/BillingLog'
import { showErrorToast, showSuccessToast } from '../services/CommonServices/alertServices'
import type { FormSubmissionRequest } from '../types/Form/FormSubmission'
import { buildSubmissionValues, getMissingWorkScopeRegistryFields } from '../utils/dynamicFormsUtils'
import type {
	UpdateWorkActivityRequest,
	WorkActivityRegistryModel,
	WorkEntryCoordinatesModel,
	WorkEntryTagFormModel,
} from '../types/Work/WorkEntry'

const DEFAULT_TAG_FORM: WorkEntryTagFormModel = {
	existingWsr: '',
	registryFieldValues: {},
}

const DEFAULT_COORDS: WorkEntryCoordinatesModel = {
	latitude: '',
	longitude: '',
}

const DEFAULT_ACTIVITY: WorkActivityRegistryModel = {
	specId: 0,
	activityType: '',
	warIdSummary: '',
	wo: '',
	billingLogEntryId: null,
	requestDate: '',
	plannedDate: '',
	plannedHrs: '',
	percentComplete: '',
	details: '',
}

export default function WorkEntryPage() {
	const [searchParams, setSearchParams] = useSearchParams()
	const [tagForm, setTagForm] = useState<WorkEntryTagFormModel>(DEFAULT_TAG_FORM)
	const [coords, setCoords] = useState<WorkEntryCoordinatesModel>(DEFAULT_COORDS)
	const [activityForm, setActivityForm] = useState<WorkActivityRegistryModel>(DEFAULT_ACTIVITY)
	const [workscopeForm, setWorkscopeForm] = useState<FormDefinitionDetail | null>(null)
	const [billingLogs, setBillingLogs] = useState<BillingLogEntryResponse[]>([])
	const [submittingWorkscope, setSubmittingWorkscope] = useState(false)
	const latestWsrLoad = useRef(0)
	const [loadedWsrTagId, setLoadedWsrTagId] = useState<number | null>(null)
	const loadedTagNumber =
		loadedWsrTagId !== null ? Number(tagForm.registryFieldValues['wsrIdTag']) : null
	const selectedProject = workscopeForm?.fields
		.find((f) => f.fieldKey === 'project')
		?.projectOptions?.find((o) => o.value === tagForm.registryFieldValues['project'])
	const [loadedSubmissionId, setLoadedSubmissionId] = useState<number | null>(null)
	const [savedBillingOrder, setSavedBillingOrder] = useState<BillingOrderValues | undefined>()

	const [activitiesLoadCount, setActivitiesLoadCount] = useState(0)
	const [activityRecords, setActivityRecords] = useState<UpdateWorkActivityRequest[]>([])

	const registryWorkOrderTrimmed = useMemo(
		() => tagForm.registryFieldValues['workOrder']?.trim() ?? '',
		[tagForm.registryFieldValues],
	)

	useEffect(() => {
		setActivityForm((prev) =>
			prev.wo === registryWorkOrderTrimmed ? prev : { ...prev, wo: registryWorkOrderTrimmed },
		)
	}, [registryWorkOrderTrimmed])

	const patchActivityForm = useCallback((patch: Partial<WorkActivityRegistryModel>) => {
		setActivityForm((p) => ({ ...p, ...patch }))
	}, [])

	const handleWorkscopeFormLoaded = useCallback(
		(form: FormDefinitionDetail, logs: BillingLogEntryResponse[]) => {
			setWorkscopeForm(form)
			setBillingLogs(logs)
		},
		[],
	)

	const patchRegistryFieldValues = useCallback((patch: Record<string, string>) => {
		setTagForm((p) => ({
			...p,
			registryFieldValues: { ...p.registryFieldValues, ...patch },
		}))
	}, [])

	const loadExistingWsr = useCallback(
		async (tagIdText: string) => {
			const scaffoldTagId = Number(tagIdText)
			if (!tagIdText || !Number.isInteger(scaffoldTagId) || scaffoldTagId <= 0) return
			if (!workscopeForm) return

			const loadVersion = ++latestWsrLoad.current
			try {
				const resp = await getWsrByTagId(scaffoldTagId)
				if (loadVersion !== latestWsrLoad.current) return
				const fieldKeyById = new Map<number, string>(
					workscopeForm.fields.map((f) => [f.id, f.fieldKey]),
				)

				const patch: Record<string, string> = {}
				for (const v of resp.values ?? []) {
					const fieldKey = fieldKeyById.get(v.formFieldId)
					if (!fieldKey) continue

					const raw =
						v.dropdownValue ??
						v.textValue ??
						(v.numberValue !== null && v.numberValue !== undefined ? String(v.numberValue) : null) ??
						(v.currencyValue !== null && v.currencyValue !== undefined
							? String(v.currencyValue)
							: null) ??
						v.dateValue ??
						v.timeValue ??
						''

					patch[fieldKey] = raw ?? ''
				}
				patch['wsrIdTag'] = String(resp.specialFields?.tagNumber ?? '')
				patch['projectOptionWorkTypeId'] =
					resp.specialFields?.projectOptionWorkTypeId != null
						? String(resp.specialFields.projectOptionWorkTypeId)
						: ''

				patchRegistryFieldValues(patch)

				const lat = resp.specialFields?.latitude
				const lng = resp.specialFields?.longitude
				setCoords((p) => ({
					...p,
					latitude: lat === null || lat === undefined ? '' : String(lat),
					longitude: lng === null || lng === undefined ? '' : String(lng),
				}))
				setLoadedWsrTagId(scaffoldTagId)
				setLoadedSubmissionId(resp.submissionId!)
				setSavedBillingOrder({ purchaseOrder: patch.purchaseOrder, workOrder: patch.workOrder })
			} catch (error: any) {
				if (loadVersion !== latestWsrLoad.current) return
				showErrorToast({
					message: error?.message || 'Failed to load WSR by tag number.',
					traceId: '',
					eventId: '',
				})
				setLoadedWsrTagId(null)
				setLoadedSubmissionId(null)
				setSavedBillingOrder(undefined)
			}
		},
		[patchRegistryFieldValues, workscopeForm],
	)

	const handleExistingWsrChange = useCallback(
		(next: string) => {
			setTagForm((p) => ({ ...p, existingWsr: next }))
			if (!next.trim()) {
				latestWsrLoad.current++
				setLoadedWsrTagId(null)
				setLoadedSubmissionId(null)
				setSavedBillingOrder(undefined)
				return
			}
			void loadExistingWsr(next)
		},
		[loadExistingWsr],
	)

	useEffect(() => {
		const tag = searchParams.get('tag')
		if (!tag || !workscopeForm) return
		handleExistingWsrChange(tag)
		setSearchParams({}, { replace: true })
	}, [searchParams, workscopeForm, handleExistingWsrChange, setSearchParams])

	const handleClear = useCallback(() => {
		latestWsrLoad.current++
		setTagForm(DEFAULT_TAG_FORM)
		setCoords(DEFAULT_COORDS)
		setActivityForm(DEFAULT_ACTIVITY)
		setActivityRecords([])
		setLoadedWsrTagId(null)
		setLoadedSubmissionId(null)
		setSavedBillingOrder(undefined)
		setSubmittingWorkscope(false)
	}, [])

	const submitWorkscopeDisabled = useMemo(() => {
		return loadedWsrTagId !== null || submittingWorkscope || !workscopeForm
	}, [loadedWsrTagId, submittingWorkscope, workscopeForm])

	const updateWorkscopeDisabled = useMemo(() => {
		return loadedWsrTagId === null || submittingWorkscope || !workscopeForm
	}, [loadedWsrTagId, submittingWorkscope, workscopeForm])

	const handleSubmitNewWorkscope = async () => {
		if (!workscopeForm) return
		const workTypeId = Number(tagForm.registryFieldValues['projectOptionWorkTypeId'])
		if (!Number.isInteger(workTypeId) || workTypeId <= 0) {
			showErrorToast({ message: 'Select a work type before creating a workscope.' })
			return
		}

		const missingRequired = getMissingWorkScopeRegistryFields(
			workscopeForm,
			tagForm.registryFieldValues,
			billingLogs,
		)

		if (missingRequired.length > 0) {
			showErrorToast({
				message: `Missing required field(s): ${missingRequired.map((f) => f.label).join(', ')}`,
				traceId: '',
				eventId: '',
			})
			return
		}

		let values: FormSubmissionRequest['values'] = []
		try {
			values = buildSubmissionValues(workscopeForm, tagForm.registryFieldValues)
		} catch (e: any) {
			showErrorToast({ message: e?.message || 'Invalid values.', traceId: '', eventId: '' })
			return
		}

		if (values.length === 0) {
			showErrorToast({ message: 'Nothing to submit.', traceId: '', eventId: '' })
			return
		}

		const lat = Number(coords.latitude)
		const lng = Number(coords.longitude)
		const hasLat = coords.latitude.trim() !== '' && !Number.isNaN(lat)
		const hasLng = coords.longitude.trim() !== '' && !Number.isNaN(lng)

		const request: FormSubmissionRequest = {
			values,
			specialFields: {
				latitude: hasLat ? lat : null,
				longitude: hasLng ? lng : null,
				projectOptionWorkTypeId:
					Number(tagForm.registryFieldValues['projectOptionWorkTypeId']) || null,
			},
		}

		try {
			setSubmittingWorkscope(true)
			const created = await submitFormSubmission(workscopeForm.id, request)
			showSuccessToast('Workscope created', true)

			const newTagIdText = created.resultEntityId != null ? String(created.resultEntityId) : ''
			if (newTagIdText) {
				setTagForm((p) => ({ ...p, existingWsr: newTagIdText }))
				await loadExistingWsr(newTagIdText)
			}
		} catch (e) {
			showErrorToast({
				message: (e as { message?: string })?.message || 'Could not save workscope.',
			})
		} finally {
			setSubmittingWorkscope(false)
		}
	}

	const handleUpdateWorkscope = async () => {
		if (!workscopeForm) return
		if (!loadedSubmissionId) return

		const missingRequired = getMissingWorkScopeRegistryFields(
			workscopeForm,
			tagForm.registryFieldValues,
			billingLogs,
			savedBillingOrder,
		)

		if (missingRequired.length > 0) {
			showErrorToast({
				message: `Missing required field(s): ${missingRequired.map((f) => f.label).join(', ')}`,
				traceId: '',
				eventId: '',
			})
			return
		}

		let values: FormSubmissionRequest['values'] = []
		try {
			values = buildSubmissionValues(workscopeForm, tagForm.registryFieldValues)
		} catch (e: any) {
			showErrorToast({ message: e?.message || 'Invalid values.', traceId: '', eventId: '' })
			return
		}

		if (values.length === 0) {
			showErrorToast({ message: 'Nothing to update.', traceId: '', eventId: '' })
			return
		}

		const lat = Number(coords.latitude)
		const lng = Number(coords.longitude)
		const hasLat = coords.latitude.trim() !== '' && !Number.isNaN(lat)
		const hasLng = coords.longitude.trim() !== '' && !Number.isNaN(lng)

		const request: FormSubmissionRequest = {
			values,
			specialFields: {
				latitude: hasLat ? lat : null,
				longitude: hasLng ? lng : null,
				projectOptionWorkTypeId:
					Number(tagForm.registryFieldValues['projectOptionWorkTypeId']) || null,
			},
		}

		try {
			setSubmittingWorkscope(true)
			await updateFormSubmission(loadedSubmissionId, request)
			setSavedBillingOrder({
				purchaseOrder: tagForm.registryFieldValues['purchaseOrder'],
				workOrder: tagForm.registryFieldValues['workOrder'],
			})
			showSuccessToast('Workscope updated', true)
		} catch (e) {
			showErrorToast({
				message: (e as { message?: string })?.message || 'Could not save workscope.',
			})
		} finally {
			setSubmittingWorkscope(false)
		}
	}

	const setDefaultActivityForm = useCallback(() => {
		setActivityForm({
			...DEFAULT_ACTIVITY,
			wo: tagForm.registryFieldValues['workOrder']?.trim() ?? '',
		})
	}, [tagForm.registryFieldValues])

	return (
		<div className='page-layout'>
			<Box sx={{ px: 2, pt: 2, width: '100%' }}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						gap: 0.5,
						mb: 1,
					}}
				>
					<Typography sx={{ fontSize: '1.5rem' }}>Work Entry Portal</Typography>
					<TimeEntryWsrTagAutocomplete
						label='Load Existing WSR'
						placeholder='Click to select...'
						value={tagForm.existingWsr || ''}
						displayValue={
							loadedTagNumber !== null
								? formatWsrTagLabel({
									projectNumber: selectedProject?.projectNumber ?? null,
									projectName: selectedProject?.projectName ?? null,
									tagNumber: loadedTagNumber,
								  })
								: ''
						}
						disabled={submittingWorkscope}
						onChange={handleExistingWsrChange}
						sx={{ width: 420, minWidth: 280, flexShrink: 0, mr: 2 }}
					/>
				</Box>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
					<WorkEntryRegistryForm
						loadedTagId={loadedWsrTagId}
						savedBillingOrder={savedBillingOrder}
						registryFieldValues={tagForm.registryFieldValues}
						onRegistryPatch={patchRegistryFieldValues}
						onClear={handleClear}
						onFormLoaded={handleWorkscopeFormLoaded}
						onSubmitNewWorkscope={handleSubmitNewWorkscope}
						onUpdateWorkscope={handleUpdateWorkscope}
						submitting={submittingWorkscope}
						submitDisabled={submitWorkscopeDisabled}
						updateDisabled={updateWorkscopeDisabled}
					/>
					<WorkEntryCoordinates
						value={coords}
						onChange={(patch: Partial<WorkEntryCoordinatesModel>) =>
							setCoords((p) => ({ ...p, ...patch }))
						}
					/>
					<WorkActivityRegistryForm
						tagNumber={loadedTagNumber}
						value={activityForm}
						onChange={patchActivityForm}
						loadedTagId={loadedWsrTagId}
						onSubmitNewWorkActivity={() => setActivitiesLoadCount(activitiesLoadCount + 1)}
						activityRecords={activityRecords}
						setDefaultActivityForm={setDefaultActivityForm}
						projectNumber={selectedProject?.projectNumber}
					/>
					<WarActivityLog
						tagNumber={loadedTagNumber}
						loadedTagId={loadedWsrTagId}
						activitiesLoadCount={activitiesLoadCount}
						activityRecords={activityRecords}
						setActivityRecords={setActivityRecords}
					/>
				</Box>
			</Box>
		</div>
	)
}
